import { useState, useEffect, useMemo, Fragment } from 'react';
import {
  Row,
  Col,
  Button,
  Spin,
  Menu,
  Form,
  Checkbox,
  Divider,
  message,
  Modal,
  Popover,
  Tooltip,
  Popconfirm
} from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { HasAccess } from "@permify/react-role";
import { getCookie } from "./../../../services/cookies";
import { useTranslation } from 'react-i18next';
import { uploadAssetController, base64ToStream } from './../../../utils/fileUtils';
import {
  consultationService,
  saveReports,
  expirationDate,
} from "../../../services/medicalConsultation";
import countrySettings from '../countrySettings.json';
import config from "../../../config";
import SidebarExamList from './SidebarExamList';
import { willTriggerDeferredExamNotification } from './utils/deferredExamUtils';

const PSYCHOLOGY_DEPARTMENT_ID = 2;

const SidebarServices = ({
  id,
  disableForm,
  loading,
  setLoading,
  servicesList,
  serviceIndex,
  patientData,
  unfinishedExams,
  countryCode,
  fetchMedicalConsultationDetails,
  medicalConsultationValidation,
  reprovedResultsMap = new Map(),
  tooltipLoadingStates = new Map(),
  examListInSidebar = false,
  examTabItems = [],
  activeExamTab,
  setActiveExamTab,
  itemHasReprovedValues,
  sidebarExamSectionRef,
  activeConsultationTab,
  deferredExamPrescriptionState,
}) => {
  const [formLoading, setFormLoading] = useState(false);
  const [serviceLoadingStates, setServiceLoadingStates] = useState(new Map());
  const [confirmReprovingValues, setConfirmReprovingValues] = useState(
    servicesList?.map((service) => ({
      id: service.id,
      type: service.type,
      show: false,
      blocked: false,
      checked: false,
    }))
  );
  const roles = getCookie('roles');
  const isMedicalDirection = roles?.includes("direccion_medica");
  const isDoctor = roles.includes('doctor');
  const isPsychology = roles.includes('equipo_psicologia');
  const isPermittedAuditorRole = (isDoctor && roles.includes('medical_auditor')) || isPsychology;
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const groupSidebarExamsByDepartment = countrySettings.medicalConsultation.groupSidebarExamsByDepartment[countryCode];
  const sidebarExamGroups = [
    {
      key: 'medicine',
      title: t('medicalConsultations.sidebarServices.medicineExamsTitle'),
      items: examTabItems.filter((item) => item.department_id !== PSYCHOLOGY_DEPARTMENT_ID),
    },
    {
      key: 'psychology',
      title: t('medicalConsultations.sidebarServices.psychologyExamsTitle'),
      items: examTabItems.filter((item) => item.department_id === PSYCHOLOGY_DEPARTMENT_ID),
    },
  ].filter((group) => group.items.length > 0);

  const setServiceLoading = (serviceId, isLoading) => {
    setServiceLoadingStates(prev => {
      const newMap = new Map(prev);
      newMap.set(serviceId, isLoading);
      return newMap;
    });
  };

  const medicalConsultationPath = (key) => {
    const integerKey = parseInt(key);
    if (servicesList[integerKey]['medical_consultation_id']) {
      return `/consulta-medica/${servicesList[integerKey]['medical_consultation_id']}`;
    } else {
      const search = '?' + new URLSearchParams({
        admissionPatient: id,
        serviceId: servicesList[integerKey]['id'],
        serviceType: servicesList[integerKey]['type']
      }).toString()
      return `/consulta-medica${search}`;
    }
  }

  const generateReport = (id, role, service) => {
    try {
      return consultationService.createMedicalConsultationReport({
        id,
        department: ['health', 'normal'].includes(service.medical_form_type) ? 'health' : 'psychological',
        role,
      });
    } catch (error) {
      throw error;
    }
  }

  const saveReportsHandler = async (id, role, service) => {
    let response;
    try {
      response = await generateReport(id, role, service);
      if (response?.pdf) {
        const [reports] = await saveReports({
          medical_consultation_detail_id: id,
          type: 'medical',
          role,
        })
        const patientDocumentNumber = patientData?.document_number;
        const patientFullName = patientData?.full_name;
        const name = {
          admin: `${patientFullName}_${patientDocumentNumber}_Informe-Medico-Resumido`,
          RRHH: `${patientFullName}_${patientDocumentNumber}_RRHH`,
        };
        await uploadAssetController({
          file: base64ToStream(`data:application/pdf;base64,${response.pdf}`, 'application/pdf', `${name[role]}.pdf`),
          object_field: 'document',
          object_id: reports,
          object_type: 'MedicalReport',
        });
      }
    } catch (error) {
      throw error;
    } finally {
      return response;
    }
  };

  const onFinish = async (values) => {
    const checkedServicesIds = values?.checkBoxServices;
    if (checkedServicesIds.length > 0) {
      let _servicesList = servicesList.filter((item) =>
        checkedServicesIds.includes(item?.id)
      );
      try {
        setFormLoading(true);

        const willTriggerDeferredEmail = willTriggerDeferredExamNotification({ deferredExamPrescriptionState });

        if (willTriggerDeferredEmail) {
          const confirmed = await new Promise(resolve => {
            Modal.confirm({
              title: 'Confirmación de envío',
              content: 'Firmar estas consultas enviará automáticamente el correo de reevaluación al paciente. ¿Desea continuar?',
              okText: 'Sí, firmar',
              cancelText: 'Cancelar',
              centered: true,
              icon: null,
              className: 'deferred-exam-confirm',
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (!confirmed) {
            setFormLoading(false);
            return;
          }
        }

        await Promise.all(_servicesList.map(async (service) => {
          if (countrySettings.medicalConsultation.setExpirationDate[countryCode]) {
            await expirationDate({ id: service.medical_consultation_id });
          }
          const params = {
            id: service.medical_consultation_id,
            department: (service.medical_form_type === 'health' || service.medical_form_type === 'normal')
              ? 'health' : 'psychological',
            ...(countryCode === 'PE' && { role: 'no' }),
          };
          const response = await consultationService.createMedicalConsultationReport(params);
          if (response?.pdf) {
            const reproved = await medicalConsultationValidation(service);
            let signerResponse = await consultationService.signServices({
              id: service.medical_consultation_id,
              ...(countrySettings.medicalConsultation.uploadReport[countryCode] && { pdf: response.pdf }),
              department: params.department,
              country_code: getCookie('country_code'),
              company_id: servicesList[serviceIndex]?.company_id,
              override: reproved,
            });
            if (countryCode === 'PE') {
              await saveReportsHandler(service.medical_consultation_id, 'RRHH', service);
              await saveReportsHandler(service.medical_consultation_id, 'admin', service);
            }
            if (signerResponse.success === true) {
              message.success({
                content: `${t('medicalConsultations.sidebarServices.medicalConsultations')} firmadas exitosamente.`,
                style: {
                  marginTop: "40vh",
                },
              });

              if (signerResponse.deferredExamNotificationFailed) {
                message.warning({
                  content: 'La consulta fue firmada exitosamente, pero hubo un problema al enviar la notificación de reevaluación. Puede reintentarlo firmando nuevamente o guardando la reevaluación.',
                  duration: 10,
                  style: {
                    marginTop: "35vh",
                  },
                });
              }
            } else {
              message.error({
                content: t('medicalConsultations.sidebarServices.errorSigned'),
                style: {
                  marginTop: '40vh'
                }
              });
            }
          } else {
            message.error({
              content: 'Ha ocurrido un error al firmar las consultas. Inténtalo nuevamente',
              style: {
                marginTop: '40vh'
              }
            });
          }
        }))
      } catch (error) {
        message.error({
          content: error || `Error al firmar ${t('medicalConsultations.sidebarServices.medicalConsultations')?.toLocaleLowerCase()}`,
          style: {
            marginTop: "40vh",
          },
        });
        console.error(error);
      } finally {
        form.resetFields();
        setFormLoading(false);
        fetchMedicalConsultationDetails();
      }
    }
  };

  const isCompanyAuditorSignerEnabled = () => {
    return countrySettings?.medicalConsultation?.auditorSignature[countryCode]
      || (config?.restrictedCompaniesSignIds?.split(',') || [])?.includes(servicesList[0]?.company_id?.toString());
  };

  const isAuditorInfoEnabled = ((isPermittedAuditorRole && isCompanyAuditorSignerEnabled()) || !isCompanyAuditorSignerEnabled());

  const isServiceTypePermitted = (item) => {
    if (isDoctor && isPsychology) {
      return true;
    } else if (isDoctor) {
      return item.medical_form_type === "health";
    } else if (isPsychology) {
      return item.medical_form_type !== "health";
    }
    return false;
  };

  const canSelectService = (item) => {
    if (!item.medical_consultation_id) {
      return false;
    }
    if (item.state !== "signed" && item.state !== "readyToSign") {
      return false;
    }
    if (item.status === "pending") {
      return false;
    }
    if (!isServiceTypePermitted(item)) {
      return false;
    }
    const hasUnfinishedExams = unfinishedExams?.some(unfinishedExam =>
      unfinishedExam.service_id === item.id
      && item.type === unfinishedExam.service_type
      && (
        unfinishedExam.measurements
        || unfinishedExam.procedures
        || unfinishedExam.missing_expert_sign
        || (!unfinishedExam.measurements && !unfinishedExam.procedures && !unfinishedExam.missing_expert_sign)
      )
    );
    if (hasUnfinishedExams) {
      return false;
    }
    return true;
  };

  const setInitialValue = async () => {
    try {
      const initialReprovingValues = servicesList.map(item => ({
        id: item.id,
        type: item.type,
        show: false,
        blocked: false,
        checked: false,
      }));

      setConfirmReprovingValues(initialReprovingValues);

      const immediateServices = servicesList
        .filter(item => {
          return !item?.status && canSelectService(item);
        })
        .map(item => item.id);

      form.setFieldsValue({ checkBoxServices: immediateServices });

      const validationPromises = servicesList
        .filter(item => item?.status)
        .map(async (item) => {
          try {
            setServiceLoading(item.id, true);
            const isBlocked = await medicalConsultationValidation(item, 'initial');

            setConfirmReprovingValues(prev =>
              prev.map(checkbox =>
                checkbox.id === item.id
                  ? { ...checkbox, blocked: isBlocked }
                  : checkbox
              )
            );

            if (canSelectService(item) && !isBlocked) {
              const current = form.getFieldValue('checkBoxServices') || [];
              if (!current.includes(item.id)) {
                form.setFieldsValue({
                  checkBoxServices: [...current, item.id]
                });
              }
            }

          } catch (error) {
            console.error(`Error validating service ${item.id}:`, error);
          } finally {
            setServiceLoading(item.id, false);
          }
        });

      await Promise.all(validationPromises);

    } catch (error) {
      message.error({
        content: 'Error al inicializar valores del Sidebar.',
        style: {
          marginTop: '40vh'
        }
      });
      console.error('Error in setInitialValue:', error);
    } finally {
      setLoading((_loading) => ({ ..._loading, sidebar: false }));
    }
  };

  const isAnyServiceLoading = useMemo(() => {
    return Array.from(serviceLoadingStates.values()).some(isLoading => isLoading);
  }, [serviceLoadingStates]);

  useEffect(() => {
    if (servicesList) {
      form.resetFields();
      setInitialValue();
    }
  }, [servicesList, loading.list])

  const checkConfirmation = (id) => {
    const blockedCheckbox = confirmReprovingValues.map((checkbox) => ({
      ...checkbox,
      checked: checkbox.id === id || checkbox.checked,
      show: false,
    }));
    const selectedSevices = form.getFieldValue('checkBoxServices');
    form.setFieldsValue({
      checkBoxServices: [...selectedSevices, id],
    });
    setConfirmReprovingValues(blockedCheckbox);
  }

  const showOrHideConfirmation = (event, id) => {
    const blockedCheckbox = confirmReprovingValues?.map(show => {
      if (show.id === id) {
        show.show = !show.show;
      }
      return show;
    });
    setConfirmReprovingValues(blockedCheckbox);
  }

  const unCheckBlocked = (id) => {
    const blockedCheckbox = confirmReprovingValues.map((checkbox) => ({
      ...checkbox,
      checked: checkbox.id === id ? false : checkbox.checked,
      show: false,
    }));
    setConfirmReprovingValues(blockedCheckbox);
  }

  const getSignStatusTooltipConfig = useMemo(() => {
    return (item) => {
      const hasReproved = (reprovedResultsMap.get(item.id) || []).length > 0;
      const isSigned = item.state === 'signed';

      return {
        title: `${isSigned ? 'Firmado' : 'No firmado'}${hasReproved ? ' con valor reprobatorio' : ''}`,
        className: `${hasReproved ? 'icon-alert-rounded' : 'icon-status-rounded'} ${isSigned ? 'signed' : 'pending'}${hasReproved ? '-reproved' : ''}`
      };
    };
  }, [reprovedResultsMap]);

  const sidebarCols = examListInSidebar
    ? (countrySettings.medicalConsultation.sidebarWidth?.[countryCode])
    : { xxl: 5, xl: 6, lg: 6 };

  return (
    <Col
      xxl={sidebarCols.xxl || 5}
      xl={sidebarCols.xl || 6}
      lg={sidebarCols.lg || 6}
      xs={24}
      className={`medical-consultation-detail__sidebar pt-50 pb-20${examListInSidebar ? ' sidebar--with-exams' : ''}`}
    >
      <Row justify="center" gutter={[0, 10]}>
        <Col lg={18} xs={22}>
          <h2 className="mb-5">{t('medicalConsultations.sidebarServices.title')}</h2>
        </Col>
        <Col lg={18} xs={22}>
          {
            loading.list ? (
              <Row justify="center">
                <Spin className="custom-spin" />
              </Row>
            ) : (
              servicesList?.length > 0 && (
                <Form
                  form={form}
                  onFinish={onFinish}
                >
                  <Form.Item name="checkBoxServices">
                    <Checkbox.Group style={{ width: "100%" }}>
                      <Menu
                        mode="inline"
                        defaultSelectedKeys={[`${serviceIndex}`]}
                        selectedKeys={[`${serviceIndex}`]}
                        style={{ width: "100%" }}
                      >
                        {servicesList.map((item, index) => {
                          const isServiceLoading = serviceLoadingStates.get(item.id);
                          const isPending = item.status === "pending";
                          const hasUnfinished = unfinishedExams?.some(unfinishedExam =>
                            unfinishedExam.service_id === item.id
                            && item.type === unfinishedExam.service_type
                            && (
                              unfinishedExam.measurements
                              || unfinishedExam.procedures
                              || unfinishedExam.missing_expert_sign
                              || (!unfinishedExam.measurements && !unfinishedExam.procedures && !unfinishedExam.missing_expert_sign)
                            )
                          );
                          const isSelectable = canSelectService(item);

                          let popoverContent = null;
                          let popoverTitle = "";
                          const blockedCheckbox = confirmReprovingValues?.find((check) => check.id === item.id)
                          if (isPending && !countrySettings.medicalConsultation.allowSignWithPending[countryCode]) {
                            popoverContent = t('medicalConsultations.sidebarServices.pendingConsultationPopoverContent');
                            popoverTitle = t('medicalConsultations.sidebarServices.pendingConsultationPopoverTitle');
                          } else if (hasUnfinished) {
                            popoverContent = <div>
                              {
                                unfinishedExams
                                  .filter(unfinishedExam => unfinishedExam.service_id === item.id && item.type === unfinishedExam.service_type)
                                  .map((unfinishedExam, idx) => (
                                    <p key={idx}>
                                      <b>{unfinishedExam.name}:</b>
                                      {
                                        unfinishedExam.measurements &&
                                        (
                                          <span>
                                            <br />
                                            {unfinishedExam.measurements.join(', ')}
                                          </span>
                                        )
                                      }
                                      {
                                        unfinishedExam.procedures &&
                                        (
                                          <span>
                                            <br />
                                            {unfinishedExam.procedures.join(', ')}
                                          </span>
                                        )
                                      }
                                      {
                                        unfinishedExam.missing_expert_sign &&
                                        <span>
                                          <br />
                                          Falta firma de especialista
                                        </span>
                                      }
                                      {
                                        (!unfinishedExam.measurements && !unfinishedExam.procedures && !unfinishedExam.missing_expert_sign)
                                        && (
                                          <span >
                                            <br />
                                            Examen no se encuentra como finalizado
                                          </span>
                                        )
                                      }
                                    </p>
                                  ))}
                            </div>;
                            popoverTitle = <h2>Exámenes pendientes</h2>;
                          }

                          const signStatusTooltipConfig = getSignStatusTooltipConfig(item);

                          return (
                            <Menu.Item key={index} className="p-0 mb-10">
                              <a href={medicalConsultationPath(index)}>
                                <Row justify="space-between" align="middle">
                                  <Col lg={22} xs={22}>
                                    <Row align="middle" className='service-row-container'>
                                      <Col flex="none">
                                        {(item.state === 'readyToSign' || item.state === 'signed') && (
                                          <>
                                            {tooltipLoadingStates.get(item.id) ? (
                                              <Tooltip
                                                title="Cargando estado..."
                                                color="#223A70"
                                                placement="right"
                                                overlayClassName="sign-status-tooltip"
                                              >
                                                <Spin size="small" />
                                              </Tooltip>
                                            ) : (
                                              <Tooltip
                                                title={signStatusTooltipConfig.title}
                                                color="#223A70"
                                                placement="right"
                                                overlayClassName="sign-status-tooltip"
                                              >
                                                <span className={signStatusTooltipConfig.className} />
                                              </Tooltip>
                                            )}
                                          </>
                                        )}
                                      </Col>
                                      <Col flex={1}>
                                        <p
                                          className="mb-0 service-name-text"
                                        >
                                          {item?.auto_generated_consultation && (
                                            <Tooltip title={t('medicalConsultations.consultationTabs.autoGenerated')}>
                                              <span className="mr-5 auditor-icon-wrapper auto-generated-consultation-state">
                                                <span className="auto-generated-badge-letter">A</span>
                                              </span>
                                            </Tooltip>
                                          )}
                                          {(countrySettings.medicalConsultation.usePrestationTitle?.[countryCode] && item.title) ? item.title : item.name}{' '}
                                          {item.extra_data?.is_operative_conditional && '(Condicional)'}
                                        </p>
                                      </Col>
                                    </Row>
                                  </Col>
                                  <Col lg={2} xs={2} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    {
                                      item.medical_consultation_id &&
                                      <HasAccess
                                        roles={['super_usuario', 'equipo_psicologia', 'doctor', 'direccion_medica']}
                                        permissions='sidebar-check-buttons'
                                      >
                                        {isServiceLoading ? (
                                          <Spin size="small" />
                                        ) : (
                                          <Fragment>
                                            {
                                              !isServiceTypePermitted(item) ? (
                                                <Checkbox
                                                  value={item.id}
                                                  disabled={true}
                                                />
                                              ) : isSelectable ? (
                                                (
                                                  (blockedCheckbox?.blocked && !blockedCheckbox?.checked) ?
                                                    (countrySettings.medicalConsultation.restrictedRoleToSignReprovingValues[countryCode] && !isMedicalDirection ? (
                                                      <Popover
                                                        trigger="hover"
                                                        content={
                                                          <div style={{ whiteSpace: 'pre-line' }}>
                                                            {t('medicalConsultations.healthForm.reprovingValuesRoleAlert', {
                                                              procedures: (reprovedResultsMap.get(item.id) || []).map(p => `- ${p.name}`).join('\n')
                                                            })}
                                                          </div>
                                                        }
                                                        overlayClassName="sidebar-tooltip"
                                                      >
                                                        <Checkbox disabled={true} />
                                                      </Popover>
                                                    ) : (
                                                      <Popconfirm
                                                        icon={<span className="icon-info-rounded presign-popconfirm-icon" />}
                                                        title={<div className="presign-popconfirm-title">
                                                          <strong>{t("medicalConsultations.healthForm.titleConfirmReprovingValues")}</strong>
                                                          <br />
                                                          <p className="presign-popconfirm-description">
                                                            {t("medicalConsultations.healthForm.descriptionConfirmReprovingValues")}
                                                          </p>
                                                        </div>}
                                                        open={blockedCheckbox?.show}
                                                        onConfirm={() => checkConfirmation(item.id)}
                                                        okButtonProps={{
                                                          className: "presign-popconfirm-button cmt-button primary",
                                                        }}
                                                        cancelButtonProps={{
                                                          className: "presign-popconfirm-button cmt-button primary outline",
                                                        }}
                                                        okText="Si"
                                                        cancelText="No"
                                                        onCancel={(event) => showOrHideConfirmation(event, item.id)}
                                                      >
                                                        <label className="ant-checkbox-wrapper ant-checkbox-wrapper ant-checkbox-wrapper-in-form-item">
                                                          <span className="ant-checkbox ant-checkbox">
                                                            <input type="checkbox" className='ant-checkbox-input'
                                                              onClick={(event) => showOrHideConfirmation(event, item.id)}
                                                              readOnly />
                                                            <span className="ant-checkbox-inner" />
                                                          </span>
                                                        </label>
                                                      </Popconfirm>
                                                    ))
                                                    :
                                                    <Checkbox
                                                      value={item.id}
                                                      onChange={() => unCheckBlocked(item.id)}
                                                    />
                                                )
                                              ) : (
                                                popoverContent ? (
                                                  <Popover
                                                    title={<h2>{popoverTitle}</h2>}
                                                    trigger="hover"
                                                    content={<b>{popoverContent}</b>}
                                                    placement="bottomLeft"
                                                    overlayClassName="sidebar-tooltip"
                                                  >
                                                    <ExclamationCircleFilled
                                                      style={{ color: "#D02A2A", fontSize: 18 }}
                                                    />
                                                  </Popover>
                                                ) : (
                                                  <ExclamationCircleFilled
                                                    style={{ color: "#D02A2A", fontSize: 18 }}
                                                  />
                                                )
                                              )
                                            }
                                          </Fragment>
                                        )}
                                      </HasAccess>
                                    }
                                  </Col>
                                </Row>
                              </a>
                            </Menu.Item>
                          );
                        })}
                      </Menu>
                    </Checkbox.Group>
                  </Form.Item>
                  {
                    servicesList?.length > 0
                    && servicesList.filter(item => canSelectService(item)).length > 0
                    && (
                      <HasAccess
                        roles={['super_usuario', 'equipo_psicologia', 'doctor', 'direccion_medica']}
                        permissions='save-medical-consultation'
                      >
                        <Button
                          loading={formLoading}
                          htmlType="submit"
                          className="cmt-button tertiary mt-40"
                          type="primary"
                          size="large"
                          disabled={disableForm || !isAuditorInfoEnabled || isAnyServiceLoading}
                        >
                          {isAnyServiceLoading ? 'Validando servicios...' : 'Firmar seleccionados'}
                        </Button>
                      </HasAccess>
                    )}
                </Form>
              )
            )}
          {examListInSidebar && activeConsultationTab === 'health' && (
            <div
              ref={sidebarExamSectionRef}
              className="sidebar-exam-list-section"
            >
              <Divider className="sidebar-exam-divider" />
              {groupSidebarExamsByDepartment ? (
                sidebarExamGroups.map((group) => (
                  <div key={group.key} className="sidebar-exam-group">
                    <h2 className="mb-10">{group.title}</h2>
                    <SidebarExamList
                      examTabItems={group.items}
                      activeExamTab={activeExamTab}
                      setActiveExamTab={setActiveExamTab}
                      itemHasReprovedValues={itemHasReprovedValues}
                      countryCode={countryCode}
                    />
                  </div>
                ))
              ) : (
                <Fragment>
                  <h2 className="mb-10">Exámenes</h2>
                  <SidebarExamList
                    examTabItems={examTabItems}
                    activeExamTab={activeExamTab}
                    setActiveExamTab={setActiveExamTab}
                    itemHasReprovedValues={itemHasReprovedValues}
                    countryCode={countryCode}
                  />
                </Fragment>
              )}
            </div>
          )}
        </Col>
      </Row >
    </Col >
  );
};

export default SidebarServices;
